import L from "leaflet";
import "leaflet/dist/leaflet.css";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import i18n from "../../i18n";
import store from "../../store";
import { conditionFormChecker, putField } from "../../utils/form";
import { createFieldInput, createFieldInputNumber, createFieldSelect } from "../input/template-builder/template-builder.logic";

const Leaflet = defineAsyncComponent(() => import("../leaflet/Leaflet"));

export const callAddressType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "ADDRESS_TYPE", value: searchQuery });
export const callProvince = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/province/cacheFindAll", {
        where: [
            { name: "nameTh", operator: "like", value: searchQuery },
            { logic: "OR", name: "nameEn", operator: "like", value: searchQuery }
        ]
    });
export const callAmphur = ({ provinceId, searchQuery }) =>
    store.dispatch("resource/v1/master/amphur/cacheFindAllByProvince", {
        provinceId,
        where: [
            { name: "nameTh", operator: "like", value: searchQuery },
            { logic: "OR", name: "nameEn", operator: "like", value: searchQuery }
        ]
    });
export const callTambon = ({ amphurId, searchQuery }) =>
    store.dispatch("resource/v1/master/tambon/cacheFindAllByAmphur", {
        amphurId,
        where: [
            { name: "nameTh", operator: "like", value: searchQuery },
            { logic: "OR", name: "nameEn", operator: "like", value: searchQuery }
        ]
    });

export const createForm = () => {
    return {
        refAddressType: null,
        addrTh: null,
        addrEn: null,
        noTh: null,
        noEn: null,
        roomTh: null,
        roomEn: null,
        buildingTh: null,
        buildingEn: null,
        floorTh: null,
        floorEn: null,
        villageTh: null,
        villageEn: null,
        mooTh: null,
        mooEn: null,
        soiTh: null,
        soiEn: null,
        roadTh: null,
        roadEn: null,
        refProvince: null,
        refAmphur: null,
        refTambon: null,
        postCode: null,
        tel: null,
        fax: null,
        latitude: null,
        longitude: null
    };
};

export const createField = ({ props, data, emit }) => {
    const { viewOnly, dataKey, locale } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = dataKey ? lodash.get(modelRef, dataKey) : modelRef;
    const prefix = dataKey ? `${dataKey}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refAddressType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refAddressType,
                            "onUpdate:value": value => {
                                model.refAddressType = value;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly,
                            list: callAddressType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            ...locale.flatMap(d => createFieldAddressInput({ props, data, emit, locale: d.name, suffix: d.suffix })),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refProvince`,
                colProp: () => ({ xs: 24, md: 8 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.refProvince"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refProvince,
                            "onUpdate:value": value => {
                                model.refProvince = value;
                                model.refAmphur = null;
                                model.refTambon = null;
                                model.postCode = null;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly,
                            list: callProvince,
                            renderLabel: ({ item }) => (
                                <div>
                                    {item?.nameTh} {item?.nameEn}
                                </div>
                            )
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refAmphur`,
                colProp: () => ({ xs: 24, md: 8 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.refAmphur"),
                inputProp: ({ field }) => {
                    const defaultDisabled = !model.refProvince || viewOnly;
                    return lodash.merge(
                        {
                            value: model.refAmphur,
                            "onUpdate:value": value => {
                                model.refAmphur = value;
                                model.refTambon = null;
                                model.postCode = null;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: defaultDisabled,
                            list: ({ searchQuery }) => callAmphur({ provinceId: model.refProvince.id, searchQuery }),
                            renderLabel: ({ item }) => (
                                <div>
                                    {item?.nameTh} {item?.nameEn}
                                </div>
                            )
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, defaultDisabled } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refTambon`,
                colProp: () => ({ xs: 24, md: 8 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.refTambon"),
                inputProp: ({ field }) => {
                    const defaultDisabled = !model.refAmphur || viewOnly;
                    return lodash.merge(
                        {
                            value: model.refTambon,
                            "onUpdate:value": value => {
                                model.refTambon = value;
                                model.postCode = value ? value.postCode : null;
                                model.isSave = true;
                                emit("updateTambon", value);
                                setTimeout(() => {
                                    emit("updateSome", model);
                                }, 100);
                            },
                            disabled: defaultDisabled,
                            list: ({ searchQuery }) => callTambon({ amphurId: model.refAmphur.id, searchQuery }),
                            renderLabel: ({ item }) => (
                                <div>
                                    {item?.nameTh} {item?.nameEn}
                                </div>
                            )
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, defaultDisabled } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}postCode`,
                colProp: () => ({ xs: 24, md: 8 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.postCode"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.postCode,
                            "onUpdate:value": value => {
                                model.postCode = value;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly,
                            comma: false,
                            maxDecimal: 5
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}tel`,
                colProp: () => ({ xs: 24, md: 8 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.tel"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.tel,
                            "onUpdate:value": value => {
                                model.tel = value;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly,
                            comma: false,
                            maxDecimal: 10
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}fax`,
                colProp: () => ({ xs: 24, md: 8 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.fax"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.fax,
                            "onUpdate:value": value => {
                                model.fax = value;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <div></div>
            },
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}latitude`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.latitude"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.latitude,
                            "onUpdate:value": value => {
                                model.latitude = value;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly,
                            allowPrecision: true,
                            maxPrecision: 8,
                            autoPrecision: false,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}longitude`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.address.longitude"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.longitude,
                            "onUpdate:value": value => {
                                model.longitude = value;
                                model.isSave = true;
                                emit("updateSome", model);
                            },
                            disabled: viewOnly,
                            allowPrecision: true,
                            maxPrecision: 8,
                            autoPrecision: false,
                            onBlur: () => useForm.validate(field.key).catch(() => {})
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}map`,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    if (data.map) {
                        const lat = model.latitude;
                        const lng = model.longitude;
                        const latLng = data.marker?.getLatLng();
                        if (!(latLng?.lat == lat && latLng?.lng == lng)) {
                            if (data.marker) {
                                data.map.removeLayer(data.marker);
                            }
                            if ((lat?.toString().length || 0) > 0 && (lng?.toString().length || 0) > 0) {
                                data.marker = L.marker([lat, lng]).addTo(data.map);
                                data.map.setZoom(15);
                                data.map.panTo(new L.LatLng(lat, lng));
                            }
                        }
                    }
                    return (
                        <Leaflet
                            style="height: 200px; z-index: 1"
                            onReady={({ map }) => {
                                data.map = map;
                            }}
                        ></Leaflet>
                    );
                }
            }
        ]
    });
};

export const createFieldAddressInput = ({ props, data, emit, locale, suffix }) => {
    const { viewOnly, dataKey } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = dataKey ? lodash.get(modelRef, dataKey) : modelRef;
    const prefix = dataKey ? `${dataKey}.` : "";
    return [
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}no${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`no${locale}`],
                        "onUpdate:value": value => {
                            model[`no${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.no${locale}`)
                            : i18n.global.t(`model.address.no${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}room${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`room${locale}`],
                        "onUpdate:value": value => {
                            model[`room${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.room${locale}`)
                            : i18n.global.t(`model.address.room${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}building${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`building${locale}`],
                        "onUpdate:value": value => {
                            model[`building${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.building${locale}`)
                            : i18n.global.t(`model.address.building${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}floor${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`floor${locale}`],
                        "onUpdate:value": value => {
                            model[`floor${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.floor${locale}`)
                            : i18n.global.t(`model.address.floor${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}village${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`village${locale}`],
                        "onUpdate:value": value => {
                            model[`village${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.village${locale}`)
                            : i18n.global.t(`model.address.village${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}moo${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`moo${locale}`],
                        "onUpdate:value": value => {
                            model[`moo${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.moo${locale}`)
                            : i18n.global.t(`model.address.moo${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}soi${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`soi${locale}`],
                        "onUpdate:value": value => {
                            model[`soi${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.soi${locale}`)
                            : i18n.global.t(`model.address.soi${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}road${locale}`,
            colProp: () => ({ xs: 24, md: 6, xxl: 3 }),
            formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model[`road${locale}`],
                        "onUpdate:value": value => {
                            model[`road${locale}`] = value;
                            model.isSave = true;
                            emit("updateSome", model);
                        },
                        disabled: viewOnly,
                        addonBefore: suffix
                            ? i18n.global.t(`model.address.road${locale}`)
                            : i18n.global.t(`model.address.road${locale}`).replace(` (${locale.toUpperCase()})`, "")
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        {
            visible: () => true,
            colProp: () => ({ xs: 24 }),
            formItemRender: () => <div></div>
        }
    ];
};

export const initForm = ({ props, data, refs, emit }) => {
    data.fields = createField({ props, data, refs, emit });
};

export const copyAddressFull = ({ from, to }) => {
    to.noTh = from.noTh;
    to.noEn = from.noEn;
    to.roomTh = from.roomTh;
    to.roomEn = from.roomEn;
    to.buildingTh = from.buildingTh;
    to.buildingEn = from.buildingEn;
    to.floorTh = from.floorTh;
    to.floorEn = from.floorEn;
    to.villageTh = from.billageTh;
    to.villageEn = from.billageEn;
    to.mooTh = from.mooTh;
    to.mooEn = from.mooEn;
    to.soiTh = from.soiTh;
    to.soiEn = from.soiEn;
    to.roadTh = from.roadTh;
    to.roadEn = from.roadEn;
    copyAddressSome({ from, to });
};

export const copyAddressSome = ({ from, to }) => {
    to.refProvince = from.refProvince;
    to.refAmphur = from.refAmphur;
    to.refTambon = from.refTambon;
    to.postCode = from.postCode;
    to.tel = from.tel;
    to.fax = from.fax;
    from.isSave = true;
    to.isSave = true;
};
